import { Button, FormLabel, HStack, Input, Select, Stack } from '@chakra-ui/react'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import type { AdditionPet, OfferProperty, OfferPropertyAddition, PackageItemModel } from 'BrokerTsLib/src/dtos'
import { PetKinds } from 'BrokerTsLib/src/dtos'
import { Insurer } from 'BrokerTsLib/src/enums'
import { getNameOfEnumKey } from 'BrokerTsLib/src/helpers/global'
import type { UseFieldArrayRemove } from 'BrokerTsLib/src/rhf'
import { useFieldArray, useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect } from 'react'
import { Add } from '../../../../../icons'

type PetProps = {
  offer: OfferProperty
  addon: PackageItemModel
}

const getDefaultItem = (addon: PackageItemModel) => {
  const defaultVariant = addon.variants.find(x => x.isDefault) ?? addon.variants[0]

  return {
    type: 'Dog',
    packageItemId: addon.packageItemId,
    variantId: defaultVariant?.id,
    limit: defaultVariant?.defaultLimit?.toString(),
    riskId: addon.riskId,
  } as AdditionPet
}

function Pet({
  offer,
  addon }: PetProps) {

  const name = 'pets'

  const {
    control,
    setValue
  } = useFormContext<OfferPropertyAddition>()

  const { append, fields, remove } = useFieldArray({
    control: control,
    name: name
  })

  // vychozi nastaveni hodnot na OfferPropertyAddition z OfferPart
  useEffect(() => {
    fields.forEach((item, index) => {
      const part = offer.parts.find(x => x.partId == item.offerPartId)
      if (!part) return

      setValue(`${name}.${index}.riskId`, part.packageItem.riskId)
      setValue(`${name}.${index}.limit`, part.limit?.toString())
      setValue(`${name}.${index}.variantId`, part.packageItemVariantId)
      setValue(`${name}.${index}.offerPartId`, part.partId)
      setValue(`${name}.${index}.packageItemId`, part.packageItem.packageItemId)
    })

    if (fields.length == 0)
      append(getDefaultItem(addon))
  }, [])

  return (
    <HStack gap={8}>
      {fields?.map((item, index) => {
        return <PetItem key={`${item.offerPartId}_${index}`} addon={addon} index={index} item={item} remove={remove} />
      })}

      {offer.insurerId == Insurer.Direct && fields.length < 4 &&
                <Stack>
                  <Button
                    variant='outline'
                    onClick={() => append(getDefaultItem(addon))}
                  >
                    <Add color="secondary.500" />
                    Další
                  </Button>
                </Stack>
      }
    </HStack >
  )
}

export default Pet

type PetItemProps = {
  item: AdditionPet,
  index: number,
  addon: PackageItemModel,
  remove: UseFieldArrayRemove
}

const PetItem = ({
  item,
  index,
  addon,
  remove
}: PetItemProps) => {
  const name = 'pets'

  const {
    register,
    setValue
  } = useFormContext<OfferPropertyAddition>()

  const handleChangeVariant = (limit: number, variantId?: number) => {
    setValue(`${name}.${index}.limit`, limit?.toString())
    setValue(`${name}.${index}.variantId`, variantId)
  }

  return (
    <Stack key={`${item.offerPartId}_${index}`} alignItems='stretch' minW='120px'>
      <FormLabel alignSelf='center'>{index + 1}. mazlíček</FormLabel>

      <Select
        size='sm'
        sx={{ width: '100%' }}
        {...register(`${name}.${index}.limit`)}
        onChange={(e) => {
          const dataset = e.target.options[e.target.selectedIndex].dataset
          handleChangeVariant(parseInt(e.target.value), parseInt(dataset.id!))
        }}
      >
        {addon.variants.map(x => (
          <option key={x.id} value={x.defaultLimit?.toString()} data-id={x.id}>
            {priceFormat(x.defaultLimit)} {x.isDefault ? '(výchozí)' : ''}
          </option>
        ))}
      </Select>

      <Select
        size='sm'
        sx={{
          width: '100%'
        }}
        {...register(`${name}.${index}.type`)}
      >
        <option value={getNameOfEnumKey(PetKinds, PetKinds.Dog)}>
          {PetKinds.Dog}
        </option>
        <option value={getNameOfEnumKey(PetKinds, PetKinds.Cat)}>
          {PetKinds.Cat}
        </option>
      </Select>

      <Input
        size='sm'
        placeholder='Plemeno'
        {...register(`${name}.${index}.race`)} />

      <Input
        size='sm'
        placeholder='Jméno'
        {...register(`${name}.${index}.name`)} />

      <Input
        size='sm'
        placeholder='Věk'
        type='number'
        {...register(`${name}.${index}.age`)} />

      <Input
        size='sm'
        placeholder='Id čipu'
        {...register(`${name}.${index}.chipId`)} />

      <Button
        size='sm'
        colorScheme='secondary'
        variant='ghost'
        onClick={() => remove(index)}
      >
        Odebrat
      </Button>
    </Stack>
  )
}
